import React, { useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link, useParams } from 'react-router-dom'

import Navigation from '../components/navigation'
import Footer from '../components/footer'
import {
  PRINCIPIA_ENTRIES,
  PRINCIPIA_MEMETICA_META,
  PRINCIPIA_VOLUMES,
} from '../data/principiaMemetica'
import { getPrincipiaCommentary } from '../data/principiaCommentary'
import './library.css'

const KIND_LABELS = {
  article: 'Field Note',
  appendix: 'Appendix',
  diagram: 'Diagram',
  paper: 'Paper',
  volume: 'Volume',
}

function normalize(text) {
  return String(text || '').toLowerCase()
}

function markdownInline(text) {
  const parts = String(text).split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g)
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={index}>{part.slice(1, -1)}</code>
    }
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
    if (link) {
      return <span key={index}>{link[1]}</span>
    }
    return part
  })
}

function renderMarkdown(content) {
  const lines = String(content || '').split('\n')
  const blocks = []
  let list = []

  const flushList = () => {
    if (list.length === 0) return
    const items = list
    list = []
    blocks.push(
      <ul key={`list-${blocks.length}`} className="library-list">
        {items.map((item, index) => (
          <li key={index}>{markdownInline(item)}</li>
        ))}
      </ul>
    )
  }

  lines.forEach((line, index) => {
    const trimmed = line.trim()
    if (!trimmed) {
      flushList()
      return
    }
    if (trimmed.startsWith('- ')) {
      list.push(trimmed.slice(2))
      return
    }
    flushList()
    if (trimmed.startsWith('### ')) {
      blocks.push(<h4 key={index}>{markdownInline(trimmed.slice(4))}</h4>)
    } else if (trimmed.startsWith('## ')) {
      blocks.push(<h3 key={index}>{markdownInline(trimmed.slice(3))}</h3>)
    } else if (trimmed.startsWith('# ')) {
      blocks.push(<h2 key={index}>{markdownInline(trimmed.slice(2))}</h2>)
    } else {
      blocks.push(<p key={index}>{markdownInline(trimmed)}</p>)
    }
  })
  flushList()
  return blocks
}

function LibraryCard({ entry, active }) {
  return (
    <Link
      to={`/library/${entry.id}`}
      className={`library-card${active ? ' is-active' : ''}`}
    >
      <span className="library-card-kind">{KIND_LABELS[entry.kind] || entry.kind}</span>
      <h3>{entry.title}</h3>
      <p>{entry.excerpt}</p>
    </Link>
  )
}

function FieldNotes({ entry }) {
  const notes = getPrincipiaCommentary(entry)
  if (!notes) return null
  const related = notes.relatedIds
    .map((id) => PRINCIPIA_ENTRIES.find((candidate) => candidate.id === id))
    .filter(Boolean)

  return (
    <section className="library-field-notes" aria-label="MDMC Field Notes">
      <div className="field-notes-head">
        <span>MDMC Field Notes</span>
        <strong>Signal, not slop</strong>
      </div>
      <div className="field-note-grid">
        <div className="field-note-card is-wide">
          <h3>Thesis</h3>
          <p>{notes.thesis}</p>
        </div>
        <div className="field-note-card">
          <h3>Mechanism</h3>
          <p>{notes.mechanism}</p>
        </div>
        <div className="field-note-card">
          <h3>Lab Note</h3>
          <p>{notes.labNote}</p>
        </div>
        <div className="field-note-card is-practice is-wide">
          <h3>Use It In The Collider</h3>
          <ul>
            {notes.practice.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
        <div className="field-note-card is-math is-wide">
          <div className="math-card-head">
            <h3>{notes.math.title}</h3>
            <span>Model</span>
          </div>
          <div className="math-formula" aria-label={`${notes.math.title} formula`}>
            {notes.math.formula}
          </div>
          <dl className="math-variable-list">
            {notes.math.variables.map(([symbol, meaning]) => (
              <div key={symbol}>
                <dt>{symbol}</dt>
                <dd>{meaning}</dd>
              </div>
            ))}
          </dl>
          <p className="math-interpretation">{notes.math.interpretation}</p>
        </div>
      </div>
      {related.length > 0 ? (
        <div className="field-note-related">
          <span>Related reactor chambers</span>
          <div>
            {related.map((item) => (
              <Link key={item.id} to={`/library/${item.id}`}>
                {item.title}
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  )
}

const Library = () => {
  const { entryId } = useParams()
  const [query, setQuery] = useState('')
  const [volume, setVolume] = useState('All')

  const filteredEntries = useMemo(() => {
    const q = normalize(query)
    return PRINCIPIA_ENTRIES.filter((entry) => {
      const notes = getPrincipiaCommentary(entry)
      const inVolume = volume === 'All' || entry.volume === volume
      if (!inVolume) return false
      if (!q) return true
      return (
        normalize(entry.title).includes(q) ||
        normalize(entry.excerpt).includes(q) ||
        normalize(entry.content).includes(q) ||
        normalize(entry.volume).includes(q) ||
        normalize(notes?.thesis).includes(q) ||
        normalize(notes?.mechanism).includes(q) ||
        normalize(notes?.practice?.join(' ')).includes(q) ||
        normalize(notes?.math?.formula).includes(q) ||
        normalize(notes?.math?.interpretation).includes(q) ||
        normalize(notes?.labNote).includes(q)
      )
    })
  }, [query, volume])

  const selected =
    PRINCIPIA_ENTRIES.find((entry) => entry.id === entryId) ||
    filteredEntries[0] ||
    PRINCIPIA_ENTRIES[0]

  return (
    <div className="library-page">
      <Helmet>
        <title>Dank Research Library — MDMC.dev</title>
        <meta
          name="description"
          content="Principia Memetica: a wiki-style research library for serious meme theory in a silly wrapper."
        />
        <meta property="og:title" content="Dank Research Library — MDMC.dev" />
        <link rel="canonical" href="https://mdmc.dev/library" />
      </Helmet>
      <Navigation />
      <main>
        <section className="library-hero">
          <span className="library-badge">PRINCIPIA MEMETICA · {PRINCIPIA_MEMETICA_META.version}</span>
          <h1>Dank Research Library</h1>
          <p>
            Serious memetic theory wearing a fake lab coat and suspiciously
            orange safety goggles. Browse the canon, cite the cringe, keep it dank.
          </p>
          <div className="library-stats">
            <span>{PRINCIPIA_MEMETICA_META.entryCount} entries</span>
            <span>{PRINCIPIA_VOLUMES.length} shelves</span>
            <span>0 slop tolerance</span>
          </div>
          <div className="library-hero-actions">
            <a className="library-glossary-cta" href="/library/appendices-glossary">
              Open the Glossary \u2014 define the terms before the theory
            </a>
          </div>
        </section>

        <section className="library-layout" aria-label="Principia Memetica library">
          <aside className="library-sidebar">
            <div className="library-search-panel">
              <label htmlFor="library-search">Search the archive</label>
              <input
                id="library-search"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="dankness, cringe, velocity…"
              />
            </div>

            <div className="library-volume-list" aria-label="Library shelves">
              {['All', ...PRINCIPIA_VOLUMES].map((name) => (
                <button
                  key={name}
                  type="button"
                  className={volume === name ? 'is-active' : ''}
                  onClick={() => setVolume(name)}
                >
                  {name}
                </button>
              ))}
            </div>

            <p className="library-sidebar-note">
              Wiki mode: no login, no hot takes hidden behind a paywall. Just
              clean memetic weirdness with citations in spirit.
            </p>
          </aside>

          <div className="library-index">
            <div className="library-index-head">
              <h2>Index</h2>
              <span>{filteredEntries.length} matches</span>
            </div>
            <div className="library-card-list">
              {filteredEntries.map((entry) => (
                <LibraryCard
                  key={entry.id}
                  entry={entry}
                  active={selected && selected.id === entry.id}
                />
              ))}
            </div>
          </div>

          <article className="library-reader">
            {selected ? (
              <>
                <div className="library-reader-meta">
                  <span>{selected.volume}</span>
                  <span>{KIND_LABELS[selected.kind] || selected.kind}</span>
                  <span>{selected.path}</span>
                </div>
                <div className="library-reader-content">
                  {renderMarkdown(selected.content)}
                </div>
                <FieldNotes entry={selected} />
              </>
            ) : (
              <p>No entries match that query. The cringe detector found nothing.</p>
            )}
          </article>
        </section>
      </main>
      <Footer />
    </div>
  )
}

export default Library
